import { BookOpen } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { hasReference, type Step } from '../../lib/explanationRefs';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';

interface Props {
  x: number;
  y: number;
  /** What was right-clicked, shown as the menu's heading. */
  title: string;
  steps: Step[];
  /** The diagram's explanation (Markdown), used to know whether the item is mentioned in it. */
  documentation: string;
  onClose: () => void;
}

/**
 * The right-click menu for diagram items. It offers "Go to explanation" when the written explanation mentions the
 * item, which opens the About tab, scrolls to that spot and flashes it. Items the explanation never mentions show
 * the entry greyed out, so it is clear why nothing happens.
 */
export default function ExplanationContextMenu({ x, y, title, steps, documentation, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });
  const available = hasReference(documentation, steps);

  // keep the menu inside the window
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setPosition({
      left: Math.max(8, Math.min(x, window.innerWidth - el.offsetWidth - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - el.offsetHeight - 8)),
    });
  }, [x, y]);

  useEffect(() => {
    const close = (e: Event) => {
      if (e instanceof MouseEvent && ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    // Registered a moment later: the right-click that opened the menu is still travelling up the page and must not close it.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('contextmenu', close);
    }, 0);
    document.addEventListener('keydown', escape);
    window.addEventListener('blur', onClose);
    window.addEventListener('resize', onClose);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('contextmenu', close);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('blur', onClose);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', left: position.left, top: position.top }}
      className="z-[60] w-64 rounded-xl border border-gray-200/80 bg-panel p-1 shadow-pop dark:border-white/10 dark:bg-gray-900"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="truncate px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{title}</div>
      <button
        role="menuitem"
        disabled={!available}
        onClick={() => {
          useDiagramViewStore.getState().requestExplanation(steps);
          onClose();
        }}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:text-gray-200 dark:hover:bg-white/10"
      >
        <BookOpen className="h-4 w-4 shrink-0 text-sky-500" />
        <span className="flex-1">Go to explanation</span>
      </button>
      {!available && <div className="px-3 pb-1.5 text-xs text-gray-400 dark:text-gray-500">The explanation does not mention this yet.</div>}
    </div>,
    document.body,
  );
}
